import { DedicationLevel } from './enums';

/**
 * Decides whether a previously saved energy balance (intakeDeltaPercent, in
 * percent, e.g. -11.2) may be restored into a regenerated program whose own
 * suggested delta is `programIntakeDelta` (as a fraction, e.g. 0.045).
 *
 * A saved deficit (negative) must not be carried into a muscle-growth program,
 * and a saved surplus (positive) must not be carried into a fat-loss program:
 * such a value is not even reachable through the energy-balance slider (its
 * range is clamped to one side of zero by the program direction), and it later
 * makes the weekly-checkup solver reject the plan ('Invalid surplus', #4037).
 * Zero ("maintain") is a legal slider position for both directions, so it is
 * always restorable. Note this deliberately differs from the solver's heal in
 * adjustForProgress, which substitutes a default even for 0: this guard asks
 * "is the value representable in the program?", the solver asks "can the
 * needed adjustment be computed from it?" — see healGoalInconsistentIntakeDelta
 * in plan-update.ts for the full reasoning.
 */
export function isIntakeDeltaCompatibleWithProgram(savedIntakeDeltaPercent: number, programIntakeDelta: number): boolean {
  if (savedIntakeDeltaPercent === 0) {
    return true;
  }
  // if the program direction is neutral, any saved value is representable
  if (programIntakeDelta === 0) {
    return true;
  }
  return Math.sign(savedIntakeDeltaPercent) === Math.sign(programIntakeDelta);
}

export function constraintIntakeDeltaPercentForDedicationLevel(intakeDeltaPercent: number, dedicationLevel: DedicationLevel) {
  switch (dedicationLevel) {
    case DedicationLevel.SlowAndSteady:
      intakeDeltaPercent = intakeDeltaPercent * 0.5;
      break;
    case DedicationLevel.Balanced:
      intakeDeltaPercent = intakeDeltaPercent * 0.75;
      break;
    default:
      break;
  }

  return intakeDeltaPercent;
}
